import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderWidth,
  borderStyle,
  borderRadius as radius,
} from "../../../design-system/tokens/borders";
import {
  fontWeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";
import {
  KG_METRIC_NODE_HEIGHT_PX,
  KG_NODE_LABEL_TEXT_GAP_PX,
  KG_NODE_PILL_MAX_WIDTH_PX,
} from "../../kg/kgNodeDimensions";
import { kgCategorySelectionShadow } from "../../kg/kgLineageTokens";
import {
  kgNodeEllipsisTextStyles,
  kgNodeTextContainerStyles,
} from "../../kg/kgNodeText.styles";
import type { KgNodeVisualState } from "../../kg/kgNodeShared.types";

/** Figma 2124-9876 — 180×60 pill, metricBlue stroke + tinted shadow */
export const Pill = styled.div<{
  $visualState: KgNodeVisualState;
  $isSelected: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing[3]};
  width: ${KG_NODE_PILL_MAX_WIDTH_PX}px;
  max-width: ${KG_NODE_PILL_MAX_WIDTH_PX}px;
  height: ${KG_METRIC_NODE_HEIGHT_PX}px;
  box-sizing: border-box;
  overflow: hidden;
  padding: ${spacing[3]} ${spacing[5]};
  border-radius: 200px;
  border: ${borderWidth[1]}
    ${({ $visualState }) =>
      $visualState === "draft" ? borderStyle.dashed : borderStyle.solid}
    ${rawColors.accent.metricBlue};
  background: ${rawColors.structural.white};
  box-shadow: ${({ $isSelected }) =>
    kgCategorySelectionShadow("metric", $isSelected)};
  outline: none;
  transition: box-shadow 0.25s ease;
  cursor: pointer;
`;

/** Figma icon container — 32×32, 8px radius, cyan tint */
export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${radius.lg};
  background: ${rawColors.accent.metricBlueBg};
  color: ${rawColors.accent.metricBlue};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

/** "Metric" + metric name — Figma inner stack gap 2px */
export const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${KG_NODE_LABEL_TEXT_GAP_PX}px;
  flex: 1;
  min-width: 0;
  ${kgNodeTextContainerStyles}
`;

export const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing[1]};
  ${kgNodeTextContainerStyles}
`;

/** Figma Overline 3 — 10px / medium / 14px line-height */
export const Label = styled.span`
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.medium};
  color: ${rawColors.text.mediumEmphasis};
  ${kgNodeEllipsisTextStyles}
  flex: 1;
  min-width: 0;
`;

/** Figma metric name — Caption 3 */
export const Name = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  ${kgNodeEllipsisTextStyles}
`;
